import type { IAnyType, Instance, IPatchRecorder } from "@gadgetinc/mobx-quick-tree";
import { createActionTrackingMiddleware2, getEnv, isActionContextThisOrChildOf, recordPatches } from "@gadgetinc/mobx-quick-tree";
import { assert, getLogger } from "./utils";

let idCounter = 0;
/** Object that queues up functions to run until a certain point, and then runs them.
 * Used to implement a callback queue for when a mobx-state-tree action actually commits, so that we don't do side effects if the action rolls back. The AtomicMiddleware actually does the smarts, but this thing holds the callbacks.
 **/
export class OperationQueue<F extends (...args: any[]) => any> {
  singletonTracker: { [key: string]: boolean } = {};
  operations: F[] = [];
  private runArgs: null | any[] = null;

  constructor(readonly id: number) {}

  register(operation: F) {
    if (this.runArgs) {
      operation(...this.runArgs);
      return;
    }

    this.operations.push(operation);
  }

  registerSingleton(key: string, operation: F) {
    if (this.runArgs) {
      operation(...this.runArgs);
      return;
    }

    if (!this.singletonTracker[key]) {
      this.register(operation);
      this.singletonTracker[key] = true;
    }
  }

  run(...args: Parameters<F>) {
    this.runArgs = args;
    for (const operation of this.operations) {
      operation(...args);
    }
  }
}

export type CommitOperation = () => void | Promise<void>;

export const onCommitQueue = (self: Instance<IAnyType>) => {
  return getEnv(self).operationQueue as OperationQueue<CommitOperation>;
};

// Copied from https://github.com/mobxjs/mobx-state-tree/blob/master/packages/mst-middlewares/src/atomic.ts
// but modified to log and setup an operation tracker
export const AtomicMiddleware = createActionTrackingMiddleware2<{ recorder: IPatchRecorder; queue: OperationQueue<CommitOperation> }>({
  filter(call) {
    // only call the methods above for actions that were not being recorded,
    // but do not call them for child acions (which inherit a copy of the env)
    if (call.env) {
      // already recording
      return false;
    }

    return true;
  },
  onStart(call) {
    const recorder = recordPatches(call.tree, (_patch, _inversePatch, actionContext) => {
      // only record patches that were generated by this action or children of this action
      return !!actionContext && isActionContextThisOrChildOf(actionContext, call.id);
    });

    const queue = new OperationQueue<CommitOperation>(idCounter++);
    getEnv(call.tree).operationQueue = queue;

    recorder.resume();
    call.env = {
      recorder,
      queue,
    };
  },
  onFinish(call, error) {
    const callEnv = assert(call.env);
    const treeEnv = getEnv(call.tree);

    callEnv.recorder.stop();

    // we purposefully don't remove the OperationQueue from the tree env so that if things register commit handlers in other middlewares that run after this one, those callbacks still run. OperationQueue will run any newly added handlers as they are added if it has already been run.

    if (error !== undefined) {
      getLogger(call.tree).error(`[MST] atomic-middleware error caught during action, rolling back. Error: ${error.message}`);
      try {
        treeEnv.rollingBack = true;
        callEnv.recorder.undo();
      } finally {
        treeEnv.rollingBack = false;
      }
    } else {
      callEnv.queue.run();
    }
  },
});
